import React from 'react';
const { useState } = React;
import { coachCall, coachingPeopleApi, copy } from './shared.jsx';
import { inputSt, labelSt, cardSt, mutedSt } from '../diagnostic/shared.jsx';
import PersonPicker from '../people/PersonPicker.jsx';
import SponsorView from '../../portal/SponsorView.jsx';
import '../../portal/portal.css';

// «Quien contrata o lidera» in a team process (the coach only). Three explicit steps, each with its confirmation:
// 1. Their person card: the usual picker (search, create, from a contact). Nothing is linked by email, name or
//    company. Changing or removing them takes this team's group view away from the one before at once; their own
//    services, their space and their access stay.
// 2. Their personal access (shared/personAccess.ts), pointed to this team (abrir=sponsor:<process>): copying only
//    reads (never renews nor reactivates a revoked one); creating is only for someone without any access.
// 3. «Ver como»: the same group view they see, read only (nothing is marked, completed or changed).
// Leading a team never gives access to quotes, payments or receipts. Earlier read-only links of the process can
// only be copied or revoked: new ones are not made (one personal access per person).

const row = { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' };
const pill = (tone) => ({ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: tone === 'ok' ? 'var(--teal-dim)' : tone === 'warn' ? '#FBF0DC' : '#EEF1F0', color: tone === 'ok' ? '#155C54' : tone === 'warn' ? '#6B4510' : '#33413E' });
const openPerson = (personId) => window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'coaching', personId } }));

export default function SponsorAccess({ view, act, people }) {
  const processId = view.process.id;
  const sponsor = view.sponsor;
  const first = String(sponsor?.name || '').split(/\s+/)[0] || 'la persona';
  const [picking, setPicking] = useState(false);
  const [chosen, setChosen] = useState(null); // a card picked, waiting for confirmation
  const [confirm, setConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState(false);
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const access = sponsor?.access;

  const link = async () => {
    const ok = await act({ action: 'setSponsorPerson', personId: chosen.id, confirm: true });
    if (ok) { setChosen(null); setConfirm(false); setPicking(false); setNote(`${chosen.name} quedó como quien contrata o lidera este equipo.`); setPreview(false); }
  };
  const remove = async () => {
    const ok = await act({ action: 'setSponsorPerson', personId: '', confirm: true });
    if (ok) { setRemoving(false); setNote('Ya no hay responsable vinculado: la vista grupal dejó de verse en su espacio.'); setPreview(false); }
  };
  const createAccess = async () => {
    setErr('');
    try { await coachCall({ action: 'personAccess', personId: sponsor.personId, op: 'create', target: `sponsor:${processId}` }); setCreating(false); await act({ action: 'getProcess' }); setNote('Acceso personal creado. Ahora podés copiarlo.'); }
    catch (e) { setErr(e.message); }
  };
  const copyLink = async () => { setNote((await copy(access.link)) ? 'Acceso copiado: abre su espacio en la vista de este equipo. Copiar no lo renueva.' : `El navegador no dejó copiar. Seleccioná y copiá: ${access.link}`); };

  return (
    <div style={cardSt}>
      <div style={{ ...row, justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>Quien contrata o lidera</h3>
        <span style={pill()}>Solo información grupal</span>
      </div>
      <p style={mutedSt}>Ve solo la vista grupal del equipo en su propio espacio: programa, objetivos compartidos, progreso de las sesiones grupales, próximas sesiones, hitos y actividades del equipo. Nunca avances, objetivos, notas, reflexiones ni mensajes individuales, ni tu panel. Ser responsable no da acceso a cotizaciones, pagos ni recibos.</p>

      <ol style={{ margin: '6px 0 0', paddingLeft: 18, display: 'grid', gap: 14 }}>
        <li style={{ display: 'grid', gap: 6 }}>
          <b style={{ fontSize: 14 }}>Su ficha de persona</b>
          {sponsor && !picking && !chosen && (
            <div style={row}>
              <span style={{ fontSize: 14 }}><b>{sponsor.name || 'Persona'}</b>{sponsor.email ? <span style={mutedSt}> · {sponsor.email}</span> : null}</span>
              <span style={pill('ok')}>Vinculada</span>
              <button className="action-btn" style={{ fontSize: 12 }} onClick={() => openPerson(sponsor.personId)}>Abrir su ficha</button>
              <button className="action-btn" style={{ fontSize: 12 }} onClick={() => { setPicking(true); setNote(''); }}>Cambiar</button>
              {!removing && <button className="action-btn" style={{ fontSize: 12 }} onClick={() => setRemoving(true)}>Quitar</button>}
            </div>
          )}
          {removing && (
            <div style={{ ...row, background: '#FBF0DC', borderRadius: 8, padding: 8 }}>
              <span style={{ fontSize: 13 }}>¿Quitar a {first}? Deja de ver este equipo en su espacio. Conserva sus propios servicios, su espacio y su acceso.</span>
              <button className="action-btn btn-primary" style={{ fontSize: 12 }} onClick={remove}>Quitar como responsable</button>
              <button className="action-btn" style={{ fontSize: 12 }} onClick={() => setRemoving(false)}>Cancelar</button>
            </div>
          )}
          {(!sponsor || picking) && !chosen && (
            <div style={{ maxWidth: 560, display: 'grid', gap: 6 }}>
              <span style={mutedSt}>Buscá su ficha, creá una nueva o partí de un contacto. Un email igual solo sugiere: nada se vincula solo.</span>
              <PersonPicker api={coachingPeopleApi(people)} onPick={(card) => { setChosen(card); setConfirm(false); }} />
              {picking && <button className="action-btn" style={{ fontSize: 12, justifySelf: 'start' }} onClick={() => setPicking(false)}>Cancelar</button>}
            </div>
          )}
          {chosen && (
            <div style={{ display: 'grid', gap: 6, background: 'rgba(30,42,44,0.03)', border: '1px solid var(--globant-border)', borderRadius: 8, padding: 10 }}>
              <span style={{ fontSize: 14 }}><b>{chosen.name}</b>{chosen.email ? <span style={mutedSt}> · {chosen.email}</span> : null}</span>
              {sponsor && <span style={{ fontSize: 13, color: '#6B4510' }}>{sponsor.name} deja de ver este equipo en su espacio (conserva sus propios servicios y su acceso).</span>}
              <label style={{ fontSize: 13, display: 'flex', gap: 6 }}><input type="checkbox" checked={confirm} onChange={e => setConfirm(e.target.checked)} /> Confirmo que {chosen.name} contrata o lidera este equipo. Ve solo la vista grupal.</label>
              <div style={row}>
                <button className="action-btn btn-primary" disabled={!confirm} onClick={link}>Vincular como «Quien contrata o lidera»</button>
                <button className="action-btn" onClick={() => { setChosen(null); setPicking(false); }}>Cancelar</button>
              </div>
            </div>
          )}
        </li>

        <li style={{ display: 'grid', gap: 6, opacity: sponsor ? 1 : 0.55 }}>
          <b style={{ fontSize: 14 }}>Su acceso personal</b>
          {!sponsor && <span style={mutedSt}>Primero vinculá su ficha.</span>}
          {sponsor && access?.active && (
            <div style={row}>
              <span style={pill('ok')}>Activo</span>
              <button className="action-btn btn-primary" style={{ fontSize: 12 }} onClick={copyLink}>Copiar acceso a la vista del equipo</button>
              <span style={mutedSt}>Copiar no lo renueva. Si también participa, entra a su mismo espacio.</span>
            </div>
          )}
          {sponsor && access?.never && (
            creating ? (
              <div style={{ ...row, background: 'rgba(30,42,44,0.03)', borderRadius: 8, padding: 8 }}>
                <span style={{ fontSize: 13 }}>Se crea su primer acceso personal (el mismo para todo su espacio). No se envía nada: lo copiás vos.</span>
                <button className="action-btn btn-primary" style={{ fontSize: 12 }} onClick={createAccess}>Crear su acceso personal</button>
                <button className="action-btn" style={{ fontSize: 12 }} onClick={() => setCreating(false)}>Cancelar</button>
              </div>
            ) : <div style={row}><span style={pill()}>Sin acceso</span><button className="action-btn" style={{ fontSize: 12 }} onClick={() => setCreating(true)}>Crear su acceso personal</button></div>
          )}
          {sponsor && access?.revoked && (
            <div style={row}><span style={pill('warn')}>Revocado</span><span style={{ fontSize: 13 }}>Copiar no lo reactiva. Para darle acceso de nuevo, «Renovar acceso» en su ficha de Personas.</span><button className="action-btn" style={{ fontSize: 12 }} onClick={() => openPerson(sponsor.personId)}>Abrir su ficha</button></div>
          )}
          {sponsor && access?.review && (
            <div style={row}><span style={pill('warn')}>Para revisar</span><span style={{ fontSize: 13 }}>Su ficha es anterior al acceso único: revisá su acceso en su ficha de Personas.</span><button className="action-btn" style={{ fontSize: 12 }} onClick={() => openPerson(sponsor.personId)}>Abrir su ficha</button></div>
          )}
        </li>

        <li style={{ display: 'grid', gap: 6, opacity: sponsor ? 1 : 0.55 }}>
          <b style={{ fontSize: 14 }}>Ver lo que verá</b>
          {sponsor ? <div style={row}><button className="action-btn" style={{ fontSize: 12 }} aria-expanded={preview} onClick={() => setPreview(v => !v)}>{preview ? 'Cerrar la vista previa' : `Ver como ${first}`}</button><span style={mutedSt}>Solo lectura: no marca nada ni cambia nada.</span></div> : <span style={mutedSt}>Primero vinculá su ficha.</span>}
        </li>
      </ol>

      {preview && sponsor && (
        <div className="portal" style={{ background: '#12121F', borderRadius: 16, padding: 16, marginTop: 12 }} aria-label={`Vista previa de lo que ve ${first}`}>
          <SponsorView load={() => coachCall({ action: 'sponsorPreview', processId })} preview onBack={() => setPreview(false)} backLabel="← Cerrar la vista previa" />
        </div>
      )}

      {view.sponsorLink && (
        <div style={{ marginTop: 14, display: 'grid', gap: 6 }}>
          <span style={labelSt}>Link anterior de solo lectura</span>
          <span style={mutedSt}>Ya no se crean links de proceso: este puede copiarse o revocarse. Lo recomendado es el acceso personal.</span>
          <div style={row}>
            <input readOnly style={{ ...inputSt, flex: 1, width: 'auto' }} value={view.sponsorLink} aria-label="Link anterior de solo lectura" />
            <button className="action-btn" onClick={() => copy(view.sponsorLink)}>Copiar</button>
            <button className="action-btn" onClick={() => window.confirm('¿Revocar el link anterior? Deja de funcionar.') && act({ action: 'sponsorLink', revoke: true })}>Revocar</button>
          </div>
        </div>
      )}
      {note && <div role="status" style={{ fontSize: 12, marginTop: 10 }}>{note}</div>}
      {err && <div role="alert" style={{ fontSize: 12, marginTop: 10, color: '#9A6212' }}>{err}</div>}
    </div>
  );
}
